<script setup lang="ts">
import { ref } from 'vue'
import { completeStreetString } from '@/helpers/tools'
import { formRules } from '@/helpers/validators'
import AnredeSelect from '@/components/anrede-select.vue'
import TelefonField from '@/components/telefon-field.vue'
import PlzOrtSelect from '@/components/plz-ort-select.vue'
import LandSelect from '@/components/land-select.vue'
import { Adresse } from '@/models/Adresse'
import KpTextfield from '@/components/kp-textfield.vue'
import type { Land } from '@/models/Land'

const {
    adresse = null,
    isProfil = false,
    loading = false,
    reducedContactData = false,
} = defineProps<{
    adresse?: Adresse
    isProfil?: boolean
    loading?: boolean
    isNew?: boolean
    reducedContactData?: boolean
}>()

const modStreet = completeStreetString
const valid = ref<boolean>(false)

// // Watcher
// watch(
//     () => value,
//     (val, oldVal) => {
//         if (val && !oldVal && isNew && personCardForm.value) {
//             personCardForm.value.resetValidation()
//         }
//     },
// )
//
</script>

<template>
    <v-skeleton-loader
        v-if="!adresse || loading"
        class="centered-card"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <div v-else>
        <v-form
            ref="personCardForm"
            v-model:model-value="valid"
            autocomplete="new-password"
            @update:model-value="$emit('validchange', valid)"
        >
            <v-card-title text-color="white">
                <v-icon class="mr-2 mb-2" left>fas fa-user</v-icon>
                {{ adresse.vorname }} {{ adresse.nachname }}
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6">
                        <AnredeSelect
                            :model-value="adresse.anrede"
                            dense
                            :label="$t('anrede')"
                            @update:model-value="
                                (value: string | null | undefined) => adresse.set('anrede', value)
                            "
                        />
                    </v-col>
                </v-row>
                <v-row wrap>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.vorname"
                            dense
                            :label="$t('vorname') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(100)]"
                            @update:model-value="(value) => adresse.set('vorname', value)"
                        ></kp-textfield>
                    </v-col>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.nachname"
                            dense
                            :label="$t('name') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(100)]"
                            @update:model-value="(value) => adresse.set('nachname', value)"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <div v-if="!isProfil && !reducedContactData">
                    <v-row>
                        <v-col cols="12" md="6">
                            <AnredeSelect
                                :model-value="adresse.anrede2"
                                dense
                                :label="$t('anrede') + ' (' + $t('2te_person') + ')'"
                                @update:model-value="
                                    (value: string | null | undefined) =>
                                        adresse.set('anrede2', value)
                                "
                            />
                        </v-col>
                    </v-row>
                    <v-row wrap>
                        <v-col cols="12" md="6">
                            <kp-textfield
                                :model-value="adresse.vorname2"
                                @update:model-value="(value) => adresse.set('vorname2', value)"
                                dense
                                :label="$t('vorname') + ' (' + $t('2te_person') + ')'"
                                :rules="[formRules.maxLength(100)]"
                            ></kp-textfield>
                        </v-col>
                        <v-col cols="12" md="6">
                            <kp-textfield
                                :model-value="adresse.nachname2"
                                @update:model-value="(value) => adresse.set('nachname2', value)"
                                dense
                                :label="$t('name') + ' (' + $t('2te_person') + ')'"
                                :rules="[formRules.maxLength(100)]"
                            ></kp-textfield>
                        </v-col>
                    </v-row>
                </div>
                <v-row v-if="!reducedContactData" wrap>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.adresszusatz"
                            @update:model-value="(value) => adresse.set('adresszusatz', value)"
                            dense
                            :label="$t('adresszusatz')"
                            :rules="[formRules.maxLength(100)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row v-if="!reducedContactData" wrap>
                    <v-col cols="8">
                        <kp-textfield
                            :model-value="adresse.strasse"
                            @update:model-value="(value) => adresse.set('strasse', value)"
                            dense
                            :label="$t('strasse') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(100)]"
                            @blur="() => adresse?.set('strasse', modStreet(adresse.strasse))"
                        ></kp-textfield>
                    </v-col>
                    <v-col cols="4">
                        <kp-textfield
                            :model-value="adresse.strasse_nr"
                            @update:model-value="(value) => adresse.set('strasse_nr', value)"
                            dense
                            :label="$t('nr') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(20)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row v-if="!reducedContactData" wrap>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.postfach"
                            @update:model-value="(value) => adresse.set('postfach', value)"
                            dense
                            :label="$t('postfach')"
                            :rules="[formRules.maxLength(20)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row v-if="!reducedContactData" wrap align="center">
                    <v-col cols="12" md="6">
                        <PlzOrtSelect
                            dense
                            :plz="adresse.plz"
                            :ort="adresse.ort"
                            @update:plz="(value) => adresse.set('plz', value)"
                            @update:ort="(value) => adresse.set('ort', value)"
                            :rules="[formRules.required]"
                        ></PlzOrtSelect>
                    </v-col>
                    <v-col cols="12" md="6">
                        <LandSelect
                            :model-value="adresse.land_id"
                            dense
                            :label="$t('land')"
                            @update:model-value="(value: number) => adresse?.set('land_id', value)"
                            @landchange="
                                (value: Land) => {
                                    adresse?.set('laendercode', value.laendercode)
                                    adresse?.set('land', value.name)
                                }
                            "
                        />
                    </v-col>
                </v-row>
                <v-row wrap>
                    <v-col cols="12" md="6">
                        <TelefonField
                            :model-value="adresse.tel_1"
                            @update:model-value="(value) => adresse.set('tel_1', value)"
                            dense
                            :label="$t('telefon') + ' 1 *'"
                            :rules="[formRules.required, formRules.maxLength(30)]"
                        ></TelefonField>
                    </v-col>
                    <v-col cols="12" md="6">
                        <TelefonField
                            :model-value="adresse.tel_2"
                            @update:model-value="(value) => adresse.set('tel_2', value)"
                            dense
                            :label="$t('telefon') + ' 2'"
                            :rules="[formRules.maxLength(30)]"
                        ></TelefonField>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.email"
                            @update:model-value="
                                (value: string | null) => adresse.set('email', value)
                            "
                            dense
                            :label="$t('email') + ' *'"
                            iconType="email"
                            :disabled="isProfil"
                            :rules="[formRules.required, formRules.email, formRules.maxLength(255)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <div style="padding: 10px 5px 5px 5px">
                    {{ $t('pflichtfelder_markiert') }}
                </div>
            </v-card-text>
        </v-form>
    </div>
</template>
